import Image from 'next/image';
import NextLink from 'next/link';
import { useState } from 'react';

import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/system/useTheme';
import { Trans, useTranslation } from 'react-i18next';

import { FabButtonAnimate, FabButtonAnimateOutlined } from '@/components/animate';
import Iconify from '@/components/iconify/Iconify';
import useResponsive from '@/hooks/useResponsive';
import {
  PATH_TALK_ADVISOR_MAIN,
  PATH_TICKET_ZOHODESK_CL,
  PATH_ZENDESK_INVOICES,
} from '@/routes/paths';
import {
  ModalBox,
  ModalBtnClose,
  ModalSpan,
  ModalSubtitle,
  ModalTitle,
} from '@/sections/products/envios99/EasyFastSecure/styles';
import { useLayoutStore } from '@/stores/layout/layout.store';
import { mapEvent } from '@/utils/goggleAnalytics/gtag';

import { registerCallEvent } from '../constants';
import { Action, FooterLinks, getInvoices, LINKS_OTHERS, media, rights } from './constants';
import {
  BoxRights,
  BusinessName,
  ButtonBox,
  ContainerBox,
  GridContainer,
  GridItem,
  GridNoPadding,
  IconMedia,
  ItemLegals,
  Line,
  LinkLegals,
  SeasonalContainer,
  StackItemRights,
  StackLinks,
  StackLinksSuperDuper,
  StackList,
  StackMedia,
  TextModalContain,
} from './styles';

import type { CountriesCode, ILinkChild, ILinks, ILinksOthersValues } from './constants';

interface HeadlineWithHrefProps {
  item: ILinksOthersValues;
  openModal?: () => void;
}

const Footer = () => {
  const [invoice, setInvoice] = useState(false);

  const country = useLayoutStore((state) => state.country);
  const isDesktop = useResponsive('up', 'md');
  const { t } = useTranslation();
  const theme = useTheme();

  const openCloseInvoice = () => setInvoice(!invoice);

  const modalAction = (action: string) => {
    if (action === 'invoice') {
      return openCloseInvoice;
    }
  };

  return (
    <ContainerBox component="footer">
      {country === 'CL' && (
        <SeasonalContainer sx={{ fontSize: '1rem' }}>
          <span>
            <b>{t('LAYOUT.HEADER.SEASONAL_CL.FOOTER.QUESTION')}</b>&nbsp;
            {t('LAYOUT.HEADER.SEASONAL_CL.FOOTER.CS')} &nbsp;
            <a href={PATH_TICKET_ZOHODESK_CL}>{t('LAYOUT.HEADER.SEASONAL_CL.MAIL')} </a>{' '}
          </span>
        </SeasonalContainer>
      )}
      <Divider />

      <Container sx={{ pt: '5vh' }}>
        <GridContainer container>
          <GridItem item xs={6} md={3}>
            <Image
              src="/logo/isotipo_99minutos.svg"
              width={49}
              height={48}
              alt="99 Minutos"
              loading="lazy"
            />

            <Typography variant="body2" color={theme.palette.primary.main} mt="1rem">
              <strong>{t('LAYOUT.FOOTER.CONTACT.TITLE')}</strong>
            </Typography>

            <Typography variant="body2" color={theme.palette.grey.main} mt="1rem">
              {t('LAYOUT.FOOTER.CONTACT.SUBTITLE')}
            </Typography>

            <Typography variant="body2" mt="1rem" mb="1rem" color={theme.palette.grey.main}>
              {t('LAYOUT.FOOTER.CONTACT.SUBTITLE_TWO')}
            </Typography>
            <ButtonBox>
              <FabButtonAnimateOutlined
                variant="outlinedExtended"
                // href={PATH_TALK_ADVISOR_MAIN}
                href={'/sales'}
                size={isDesktop ? 'medium' : 'large'}
                // target="_blank"
                data-cy="btn-schedule-call"
                onClick={() => mapEvent(registerCallEvent)}
              >
                {t('COMMON.ACTIONS.CALL_3')}
              </FabButtonAnimateOutlined>
            </ButtonBox>

            <StackMedia>
              {media[country]?.map(({ path, name, url, event }) => (
                <Link
                  href={url}
                  data-cy="btn-social-media"
                  key={name}
                  target="_blank"
                  onClick={() => mapEvent(event)}
                >
                  <IconMedia key={name} src={`/assets/images/social/${path}`} alt={name} />
                </Link>
              ))}
            </StackMedia>
          </GridItem>
          <Grid item xs={12} md={6}>
            <StackLinks>
              {FooterLinks.map((list) =>
                list.countries.includes(country as CountriesCode) ? (
                  <FooterLinksComponent {...list} key={list.headline} />
                ) : null,
              )}

              <StackLinksSuperDuper>
                <GridNoPadding container spacing={1.5} sx={{ paddingX: { xs: '2rem', md: 0 } }}>
                  {LINKS_OTHERS[country]?.map((list) => (
                    <Grid item xs={12} md={12} key={list.headline}>
                      <HeadlineWithHref
                        item={list}
                        openModal={
                          list.action === Action.Modal ? modalAction(list.modal) : undefined
                        }
                      />
                    </Grid>
                  ))}
                </GridNoPadding>
              </StackLinksSuperDuper>
            </StackLinks>
          </Grid>
        </GridContainer>

        <Divider />

        <BoxRights>
          {country === 'CL' && <BusinessName>{t('LAYOUT.FOOTER.LEGAL.BUSINESSNAME')}</BusinessName>}
          <ItemLegals copyright>{t('LAYOUT.FOOTER.LEGAL.COPYRIGHT')}</ItemLegals>

          <StackItemRights>
            {rights.map((item, index) => {
              return (
                item.countries.includes(country as CountriesCode) && (
                  <LinkLegals
                    key={item.name}
                    href={item.path}
                    data-cy="btn-legals-footer"
                    onClick={() => mapEvent(item.event)}
                  >
                    <ItemLegals key={item.name} copyright={false}>
                      {t(item.name)}
                    </ItemLegals>
                  </LinkLegals>
                )
              );
            })}
          </StackItemRights>
        </BoxRights>

        <Dialog
          fullWidth
          maxWidth="md"
          open={invoice}
          onClose={openCloseInvoice}
          scroll="body"
          fullScreen={!isDesktop}
        >
          <ModalInvoices openCloseModal={openCloseInvoice} />
        </Dialog>
      </Container>
    </ContainerBox>
  );
};

const HeadlineWithHref = ({ item, openModal }: HeadlineWithHrefProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (item.action === Action.Link) {
    return (
      <Link
        component={NextLink}
        href={item.href}
        target={item.target}
        color="inherit"
        variant="body2"
        data-cy={`btn-menu-headline-${item.headline}`}
        onClick={() => mapEvent(item.event)}
      >
        <Typography fontWeight="bold" variant="body2" color={theme.palette.secondary.main}>
          {t(item.headline)}
        </Typography>
      </Link>
    );
  }

  const action = () => {
    mapEvent(item.event);
    openModal?.();
  };

  if (item.action === Action.Modal) {
    return (
      <Link
        component="button"
        onClick={action}
        color="inherit"
        data-cy={`btn-menu-headline-${item.headline}`}
      >
        <Typography fontWeight="bold" variant="body2" color={theme.palette.secondary.main}>
          {t(item.headline)}
        </Typography>
      </Link>
    );
  }

  return <></>;
};

const FooterLinksComponent = (list: ILinks) => {
  const country = useLayoutStore((state) => state.country);
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <StackList key={list.headline}>
      <Typography fontWeight="bold" variant="body2" color={theme.palette.secondary.main}>
        {t(list.headline)}
      </Typography>

      {list.children
        .filter((child: ILinkChild) => child.countries?.includes(country))
        .map((link: ILinkChild) =>
          link.href.length > 0 ? (
            country === 'CL' && t(link.name) === 'fulfill99' ? null : (
              <Link
                key={link.name}
                component={NextLink}
                href={link.href}
                color="inherit"
                variant="body2"
                data-cy="btn-menu-footer"
                target={link.external === true ? '_blank' : ''}
                onClick={() => mapEvent(link.event)}
              >
                {t(link.name)}
              </Link>
            )
          ) : (
            <Typography variant="body2" key={link.name}>
              {t(link.name)}
            </Typography>
          ),
        )}
    </StackList>
  );
};
interface ModalInvoicesProps {
  openCloseModal: () => void;
}

const ModalInvoices = ({ openCloseModal }: ModalInvoicesProps) => {
  const { t } = useTranslation();
  const country: CountriesCode = useLayoutStore((state) => state.country as CountriesCode);
  const countryName = useLayoutStore((state) => state.countryName);
  const invoiceModal = getInvoices[country];
  const isDesktop = useResponsive('up', 'md');
  const size = isDesktop ? 35 : 30;

  const subtitleValues = {
    email: invoiceModal.mailto != null ? t(invoiceModal.mailto) : '',
    emailCopy: invoiceModal.mailtoCC != null ? t(invoiceModal.mailtoCC) : '',
  };

  return (
    <ModalBox>
      <ModalBtnClose
        onClick={openCloseModal}
        sx={{ position: 'absolute', top: '1rem', right: '1rem' }}
      >
        <Iconify icon={'eva:close-outline'} color="primary.main" width={size} />
      </ModalBtnClose>

      <ModalTitle>
        {t('GET_INVOICES.TITLE')}
        <ModalSpan>{countryName}</ModalSpan>
      </ModalTitle>

      <ModalSubtitle>{t('GET_INVOICES.SUBTITLE')}</ModalSubtitle>

      {invoiceModal.subtitle !== undefined && (
        <span>
          <Trans i18nKey={invoiceModal.subtitle} values={subtitleValues}>
            aa
            <Link href={`mailto:${subtitleValues.email}`}>email</Link>
            cc
            <Link href={`mailto:${subtitleValues.emailCopy}`}>emailCopy</Link>
          </Trans>
        </span>
      )}

      <TextModalContain>
        {invoiceModal.items.map((item) => {
          return typeof item !== 'string' ? (
            <>
              <p>
                <strong>{t(item.title ?? '')}</strong>
              </p>
              {item.children?.map((child) => (
                <Line left="1.2rem" key={child}>
                  {t(child)}
                </Line>
              ))}
            </>
          ) : (
            <Line left="0">{t(item)}</Line>
          );
        })}
      </TextModalContain>

      <FabButtonAnimate
        variant="extended"
        size="medium"
        color="primary"
        href={PATH_ZENDESK_INVOICES}
        target="_blank"
        data-cy="btn-call-business-advisor"
      >
        {t('GET_INVOICES.BUTTON')}
      </FabButtonAnimate>
    </ModalBox>
  );
};

export default Footer;
